import {
  type ID,
  selectRootNodesBySection,
} from '@octopus-do/octopus-project-core'
import type { JumpToPayload } from '@store/globalSaga/types'
import type { artBoardActions } from '@store/reducers/artboard'
import { call, delay, put, select } from 'redux-saga/effects'

import { GlobalActions } from '@/store'

interface Box {
  bottom: number
  left: number
  right: number
  top: number
}

export const isInsideViewport = (rect: Box, viewport: Box): boolean =>
  rect.left >= viewport.left &&
  rect.top >= viewport.top &&
  rect.right <= viewport.right &&
  rect.bottom <= viewport.bottom

const isTargetVisible = ({ selector, value }: JumpToPayload): boolean => {
  const rect = document
    .querySelector(`[${selector}="${value}"]`)
    ?.getBoundingClientRect()

  if (!rect) {
    return false
  }

  return isInsideViewport(rect, {
    bottom: window.innerHeight,
    left: 0,
    right: window.innerWidth,
    top: 0,
  })
}

/**
 * Brings a section into view.
 *
 * Aims at the section's first root page rather than at the section box: an import can
 * drop three pages or three hundred, and centring the box would leave a large one showing
 * its middle, with the heading somewhere off screen. The first root page is the section's
 * top-left corner whatever its size.
 *
 * With `skipWhenVisible` the camera stays put if that target is already fully on screen:
 * a duplicated section lands right under its original, and jumping to something the user
 * can see reads as a glitch.
 */
export function* revealSection({
  payload: { id, skipWhenVisible = false },
}: ReturnType<typeof artBoardActions.revealSection>) {
  const rootNodeIds: ID[] = yield select(selectRootNodesBySection(id))

  // The pages were added in the same tick; the jump measures the DOM, which React has not
  // written yet. Same wait as jumpToThread.
  yield delay(1)

  const target: JumpToPayload =
    rootNodeIds.length === 0
      ? { selector: 'id', value: `section_${id}` }
      : { selector: 'data-scroll-id', value: rootNodeIds[0] }

  if (skipWhenVisible) {
    const visible: boolean = yield call(isTargetVisible, target)

    if (visible) {
      return
    }
  }

  yield put(GlobalActions.jumpTo(target))
}
